import React, { useState } from 'react';

import {
  IntakeFormDefinitionSchema,
  IntakeFormValuesSchema,
  validateIntakeFormValues,
} from '@aline/common';
import type { IntakeFormDefinition, IntakeFormFileValue, Leaves, Paths } from '@aline/common';
import { InputLabel, Paper, Typography } from '@mui/material';
import { FieldArray, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FormikAlineInput, FormikAlineTextarea, LoadingState } from 'src/components';
import { COMPLETE_REQUIRED_INTAKE_QUESTIONS_MSG } from 'src/components/workflow/workflow-detail/intake-form/intake-form-validation-messages';
import { Apollo } from 'src/gql/generated';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { LaunchFooter } from './LaunchFooter';
import { useRequiredFieldNavigation } from './required-field-navigation';
import { LaunchDocumentFields } from '../../components/quick-start/components/LaunchDocumentFieldsStep';
import { draftFieldsFromDefinition } from '../../components/workflow/workflow-detail/intake-form/intake-form-fields';
import { IntakeFormFields } from '../../components/workflow/workflow-detail/intake-form/IntakeFormFields';

/** Matches the width of the branded header card on LaunchPage so the page reads as one column. */
const CONTENT_WIDTH = 832;

const useStyles = makeStyles()(theme => ({
  addDetails: {
    background: theme.palette.common.white,
    borderRadius: 8,
    padding: theme.spacing(3),
    width: '100%',
  },
  assigneeInput: {
    flex: 1.5,
  },
  detailsSection: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
  },
  form: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    justifyContent: 'center',
    width: CONTENT_WIDTH,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    justifyContent: 'center',
  },
  hzInputWrapper: {
    alignItems: 'center',
    background: theme.palette.common.white,
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(1),
    width: '100%',
  },
  inputLabel: {
    flex: 1,
    ...theme.appTypography.fieldLabel,
    margin: theme.spacing(1, 0),
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  instructionsContainer: {
    width: CONTENT_WIDTH,
  },
  listItem: {
    ...getTypographyForSx(theme.typography.body1),
    marginBottom: theme.spacing(1),
  },
  noLaunchPage: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
  },
  placeholderList: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  step: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    borderRadius: '50%',
    display: 'flex',
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  textWhite: {
    color: theme.palette.common.white,
  },
  title: {
    padding: theme.spacing(1),
  },
}));
const hints = {
  companyName: 'Please enter your name',
  email: 'Please enter a valid email',
};
interface PlaceholderFormAssignment {
  email: string;
  placeholder: { id: UUID; name: string };
}
interface LaunchConfigForm {
  alineFieldValues: Apollo.AlineFieldValueInput[];
  intakeFormValues: Record<string, unknown>;
  launchingUserCompanyName: string;
  note: string;
  placeholderAssignments: PlaceholderFormAssignment[];
  requesterEmail: string;
}
const noteKey: Leaves<LaunchConfigForm> = 'note';
const emailKey: Leaves<PlaceholderFormAssignment> = 'email';
const requesterEmailKey: Leaves<LaunchConfigForm> = 'requesterEmail';
const placeholderAssignmentsKey: Paths<LaunchConfigForm> = 'placeholderAssignments';
const launchingUserCompanyNameKey: Leaves<LaunchConfigForm> = 'launchingUserCompanyName';

const LaunchWorkflowConfigSchema = Yup.object<LaunchConfigForm>({
  alineFieldValues: Yup.array(),
  intakeFormValues: Yup.object(),
  launchingUserCompanyName: Yup.string().required(hints.companyName),
  note: Yup.string(),
  placeholderAssignments: Yup.array().of(
    Yup.object<PlaceholderFormAssignment>({
      email: Yup.string().email(hints.email).required(hints.email),
    })
  ),
  requesterEmail: Yup.string().email(hints.email).required(hints.email),
});

export function LaunchConfigForm({
  afterSubmit,
  launchConfigId,
}: {
  afterSubmit: (
    launchedWorkflow: NonNullable<
      Apollo.LaunchWorkflowViaConfigMutationResult['data']
    >['launchWorkflowViaConfig']['workflow']
  ) => void;
  launchConfigId: UUID;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const {
    jumpToNextRequiredField,
    jumpToPreviousRequiredField,
    requiredFieldCount,
    setContainerEl,
  } = useRequiredFieldNavigation();
  const [showRequiredErrorsToken, setShowRequiredErrorsToken] = useState(0);
  const { data, loading } = Apollo.useGetWorkflowLaunchConfigQuery({
    variables: {
      launchConfigId,
    },
  });
  const [launchWorkflowViaConfig] = Apollo.useLaunchWorkflowViaConfigMutation();
  const [createFilesViaLaunchConfig] = Apollo.useCreateFilesViaLaunchConfigMutation();
  const [deleteFilesViaLaunchConfig] = Apollo.useDeleteFilesViaLaunchConfigMutation();
  const workflowLaunchConfig = data?.workflowLaunchConfig;

  if (loading) return <LoadingState />;

  if (!workflowLaunchConfig?.enabled)
    return (
      <div className={classes.noLaunchPage}>
        <Typography variant="h1">
          {!workflowLaunchConfig
            ? 'No launch page found.'
            : 'Launch page is disabled. Contact owner to enable.'}
        </Typography>
      </div>
    );

  const launchForm = workflowLaunchConfig.launchForm;
  const intakeForm = launchForm.intakeForm;
  const parsedIntakeDefinition = IntakeFormDefinitionSchema.safeParse(intakeForm?.formDefinition);
  const intakeDefinition = parsedIntakeDefinition.success ? parsedIntakeDefinition.data : null;
  const intakeFields = intakeDefinition ? draftFieldsFromDefinition(intakeDefinition) : [];
  const placeholderAssignments = workflowLaunchConfig.placeholders.flatMap<
    LaunchConfigForm['placeholderAssignments'][number]
  >(p => {
    if (!p.role?.id) return [];
    return [
      {
        email: '',
        placeholder: { id: p.role.id, name: p.role.roleName },
      },
    ];
  });
  const stepNumbers = getLaunchStepNumbers({
    hasDocumentFields: launchForm.alineFields.length > 0,
    hasIntakeFields: intakeFields.length > 0,
    hasPlaceholders: placeholderAssignments.length > 0,
  });

  return (
    <>
      <Typography align="center" component={'h2'} variant="h1">
        {`Welcome to ${workflowLaunchConfig.workflowTitle} workflow!`}
      </Typography>
      <LaunchInstructions placeholderStep={stepNumbers.placeholders} />
      <Formik<LaunchConfigForm>
        enableReinitialize
        initialValues={{
          alineFieldValues: [],
          intakeFormValues: getInitialIntakeValues(intakeForm),
          launchingUserCompanyName: '',
          note: '',
          placeholderAssignments,
          requesterEmail: '',
        }}
        validationSchema={LaunchWorkflowConfigSchema}
        onSubmit={async (form, { setSubmitting }) => {
          const {
            alineFieldValues,
            intakeFormValues,
            launchingUserCompanyName,
            note,
            placeholderAssignments,
            requesterEmail,
          } = form;
          /**
           * The snackbar and inline required errors are raised by the form's
           * submit handler, which runs even when the Yup schema fails. This is
           * only the gate that keeps an incomplete intake form from launching.
           */
          if (getHasIntakeProblems(intakeDefinition, intakeFormValues)) {
            setSubmitting(false);
            return;
          }

          const { data } = await launchWorkflowViaConfig({
            variables: {
              input: {
                alineFieldValues,
                intakeFormValues,
                launchConfigId,
                placeholderAssignments: placeholderAssignments.map(
                  (assignment): Apollo.PlaceholderAssignmentInput => ({
                    email: assignment.email,
                    userDefinedRoleId: assignment.placeholder.id,
                  })
                ),
                requesterCompanyName: launchingUserCompanyName,
                requesterEmail,
                requesterNote: note,
              },
            },
          });
          setSubmitting(false);
          if (data?.launchWorkflowViaConfig.workflow)
            afterSubmit(data.launchWorkflowViaConfig.workflow);
        }}
      >
        {({ handleReset, handleSubmit, isSubmitting, setFieldValue, values }): JSX.Element => {
          const valueByFieldId = new Map(
            values.alineFieldValues.map(field => [field.fieldId, field.value])
          );
          const createIntakeFiles = async (
            records: Apollo.CreateFilesMutationVariables['input']['records']
          ): Promise<IntakeFormFileValue[]> => {
            const { data } = await createFilesViaLaunchConfig({
              variables: { input: { launchConfigId, records } },
            });
            return data?.createFilesViaLaunchConfig ?? [];
          };
          const removeIntakeFile = async (file: IntakeFormFileValue) => {
            await deleteFilesViaLaunchConfig({
              variables: { input: { documentIds: [file.documentId], launchConfigId } },
            });
          };
          /**
           * Formik skips `onSubmit` entirely when the Yup schema fails, so intake
           * feedback has to fire here — otherwise a missing name or placeholder
           * email would swallow the snackbar and leave incomplete intake fields
           * unmarked. Formik still validates and surfaces its own field errors.
           */
          const submitLaunch = (event: React.FormEvent<HTMLFormElement>) => {
            if (getHasIntakeProblems(intakeDefinition, values.intakeFormValues)) {
              enqueueSnackbar(COMPLETE_REQUIRED_INTAKE_QUESTIONS_MSG, { variant: 'warning' });
              setShowRequiredErrorsToken(token => token + 1);
            }
            handleSubmit(event);
          };

          return (
            <form
              ref={setContainerEl}
              className={classes.form}
              onReset={handleReset}
              onSubmit={submitLaunch}
            >
              <LaunchFormSection step={stepNumbers.details} title="Add Your Details">
                <Paper className={classes.addDetails}>
                  <div
                    className={classes.inputWrapper}
                    data-required-field={
                      values.launchingUserCompanyName.trim() === '' ? '' : undefined
                    }
                  >
                    <InputLabel className={classes.inputLabel}>Name</InputLabel>
                    <FormikAlineInput
                      fieldName={launchingUserCompanyNameKey}
                      placeholder="Type your individual or company name..."
                    />
                  </div>
                  <div
                    className={classes.inputWrapper}
                    data-required-field={values.requesterEmail.trim() === '' ? '' : undefined}
                  >
                    <InputLabel className={classes.inputLabel}>Email</InputLabel>
                    <FormikAlineInput
                      fieldName={requesterEmailKey}
                      placeholder="Enter your email..."
                    />
                  </div>
                  <div className={classes.inputWrapper}>
                    <InputLabel className={classes.inputLabel}>Add a Note</InputLabel>
                    <FormikAlineTextarea
                      fieldName={noteKey}
                      maxRows={5}
                      minRows={5}
                      placeholder="Add a message..."
                    />
                  </div>
                </Paper>
              </LaunchFormSection>
              {stepNumbers.placeholders !== null && (
                <LaunchFormSection step={stepNumbers.placeholders} title="Assign Placeholders">
                  <div className={classes.placeholderList}>
                    <PlaceholderAssignments assignments={values.placeholderAssignments} />
                  </div>
                </LaunchFormSection>
              )}
              {intakeForm && stepNumbers.intakeFields !== null && (
                <LaunchFormSection step={stepNumbers.intakeFields} title="Intake Questions">
                  <Paper className={classes.addDetails}>
                    <IntakeFormFields
                      columns={2}
                      createFiles={createIntakeFiles}
                      fields={intakeFields}
                      formDefinition={intakeForm.formDefinition}
                      showRequiredErrorsToken={showRequiredErrorsToken}
                      values={values.intakeFormValues}
                      onChange={(name, value) =>
                        setFieldValue('intakeFormValues', {
                          ...values.intakeFormValues,
                          [name]: value,
                        })
                      }
                      onRemoveFile={removeIntakeFile}
                    />
                  </Paper>
                </LaunchFormSection>
              )}
              {stepNumbers.documentFields !== null && (
                <LaunchFormSection step={stepNumbers.documentFields} title="Document Fields">
                  <Paper className={classes.addDetails}>
                    <LaunchDocumentFields
                      alineFields={launchForm.alineFields}
                      columns={2}
                      documentsList={launchForm.documentsList}
                      valueByFieldId={valueByFieldId}
                      onChange={(fieldId, value) =>
                        setFieldValue(
                          'alineFieldValues',
                          upsertAlineFieldValue(values.alineFieldValues, fieldId, value)
                        )
                      }
                    />
                  </Paper>
                </LaunchFormSection>
              )}
              <LaunchFooter
                isSubmitting={isSubmitting}
                requiredFieldCount={requiredFieldCount}
                onJumpToNextRequiredField={jumpToNextRequiredField}
                onJumpToPreviousRequiredField={jumpToPreviousRequiredField}
              />
            </form>
          );
        }}
      </Formik>
    </>
  );
}

function getHasIntakeProblems(
  intakeDefinition: IntakeFormDefinition | null,
  intakeFormValues: Record<string, unknown>
): boolean {
  if (!intakeDefinition) return false;
  const validation = validateIntakeFormValues(intakeDefinition, intakeFormValues);
  if (validation.missingRequiredFields.length > 0) return true;
  return Object.keys(validation.fieldErrors ?? {}).length > 0;
}

interface LaunchStepNumbers {
  details: number;
  documentFields: number | null;
  intakeFields: number | null;
  placeholders: number | null;
}

/**
 * Numbers only the sections that actually render, so a launch page without
 * placeholders (or without an intake form) still reads 1, 2, 3 in order.
 */
function getLaunchStepNumbers({
  hasDocumentFields,
  hasIntakeFields,
  hasPlaceholders,
}: {
  hasDocumentFields: boolean;
  hasIntakeFields: boolean;
  hasPlaceholders: boolean;
}): LaunchStepNumbers {
  const details = 1;
  const placeholders = hasPlaceholders ? details + 1 : null;
  const intakeFields = hasIntakeFields ? (placeholders ?? details) + 1 : null;
  const documentFields = hasDocumentFields ? (intakeFields ?? placeholders ?? details) + 1 : null;

  return { details, documentFields, intakeFields, placeholders };
}

function LaunchFormSection({
  children,
  step,
  title,
}: {
  children: React.ReactNode;
  step: number;
  title: string;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.detailsSection}>
      <div className={classes.header}>
        <div className={classes.step}>
          <Typography className={classes.textWhite}>{step}</Typography>
        </div>
        <Typography align="center" className={classes.title} component="h3" variant="h2">
          {title}
        </Typography>
      </div>
      {children}
    </div>
  );
}

function getInitialIntakeValues(
  intakeForm: NonNullable<
    Apollo.GetWorkflowLaunchConfigQuery['workflowLaunchConfig']
  >['launchForm']['intakeForm']
): Record<string, unknown> {
  const parsed = IntakeFormValuesSchema.safeParse(intakeForm?.defaultValues);
  return parsed.success ? parsed.data : {};
}

function upsertAlineFieldValue(
  current: Apollo.AlineFieldValueInput[],
  fieldId: string,
  value: string
): Apollo.AlineFieldValueInput[] {
  const without = current.filter(entry => entry.fieldId !== fieldId);
  if (value === '') return without;
  return [...without, { fieldId, value }];
}

function PlaceholderAssignments({
  assignments,
}: {
  assignments: PlaceholderFormAssignment[];
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <FieldArray
      name={placeholderAssignmentsKey}
      render={() => {
        return (
          <>
            {assignments.map((assignment, index) => {
              return (
                <Paper
                  key={index}
                  className={classes.hzInputWrapper}
                  data-required-field={assignment.email.trim() === '' ? '' : undefined}
                >
                  <InputLabel className={classes.inputLabel}>
                    {assignment.placeholder.name}
                  </InputLabel>
                  <FormikAlineInput
                    className={classes.assigneeInput}
                    displayErrorText={false}
                    fieldName={`${placeholderAssignmentsKey}.${index}.${emailKey}`}
                    helperText=""
                    placeholder="Assignee’s e-mail"
                  />
                </Paper>
              );
            })}
          </>
        );
      }}
    />
  );
}

function LaunchInstructions({ placeholderStep }: { placeholderStep: number | null }): JSX.Element {
  const { classes } = useStyles();
  const instructionSteps = [
    'You will receive a notification immediately and a copy of all documents.',
    placeholderStep === null
      ? 'Recipients are notified at the appropriate time to complete the documents, sign, or do other actions.'
      : `Recipients listed in Step ${placeholderStep} are notified at the appropriate time to complete the documents, sign, or do other actions.`,
    'After the workflow is complete, everyone will receive a copy of the signed documents automatically.',
  ];

  return (
    <div className={classes.instructionsContainer}>
      <Typography>
        Please follow the instructions below to launch your workflow. Here is what you can expect
        when the workflow is launched:
      </Typography>
      <ol>
        {instructionSteps.map((instructions, index) => {
          return (
            <li key={index} className={classes.listItem}>
              <Typography>{instructions}</Typography>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
